// LeaveMaster.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AddLeaveMaster from './AddLeaveMaster';
import EditLeaveMaster from './EditLeaveMaster';

const LeaveMaster = () => {
  const [employees, setEmployees] = useState([]);
  const [leaveMaster, setLeaveMaster] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedLeaveMaster, setSelectedLeaveMaster] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [userPermissions, setUserPermissions] = useState({
    can_view: false,
    can_add: false,
    can_edit: false,
    can_delete: false,
    loaded: false
  });

  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

  // Fetch user permissions
  const fetchUserPermissions = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setUserPermissions({
          can_view: false,
          can_add: false,
          can_edit: false,
          can_delete: false,
          loaded: true
        });
        return;
      }

      const response = await axios.get(`${API_BASE}/get_user_permissions.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.permissions) {
        const leavePermissions = response.data.permissions.find(
          p => p.module_name === 'LeaveMaster' || p.module_name === 'LeaveManagement'
        );

        if (leavePermissions) {
          setUserPermissions({
            can_view: Boolean(leavePermissions.can_view),
            can_add: Boolean(leavePermissions.can_add),
            can_edit: Boolean(leavePermissions.can_edit),
            can_delete: Boolean(leavePermissions.can_delete),
            loaded: true
          });
        } else {
          setUserPermissions({
            can_view: true,
            can_add: false,
            can_edit: false,
            can_delete: false,
            loaded: true
          });
        }
      } else {
        setUserPermissions({
          can_view: true,
          can_add: true,
          can_edit: true,
          can_delete: true,
          loaded: true
        });
      }
    } catch (error) {
      setUserPermissions({
        can_view: true,
        can_add: true,
        can_edit: true,
        can_delete: true,
        loaded: true
      });
    }
  }, [API_BASE]);

  const fetchEmployees = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        return;
      }

      const response = await axios.get(`${API_BASE}/get_employees.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setEmployees(response.data.employees || []);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      setEmployees([]);
      console.error('Error fetching employees:', error);
    }
  }, [API_BASE]);

  const fetchLeaveMaster = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE}leave/get_leave_master.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setLeaveMaster(response.data.leave_master || []);
        setError('');
      } else {
        setLeaveMaster([]);
        const errorMsg = response.data?.error || 'Failed to fetch leave master data';
        setError(errorMsg);
      }
    } catch (error) {
      setLeaveMaster([]);
      const errorMsg = error.response?.data?.error ||
        error.message ||
        'Failed to fetch leave master data';
      setError(errorMsg);
      console.error('Error fetching leave master:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await axios.get(`${API_BASE}leave/get_leave_types.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 10000
      });

      if (response.data && response.data.success) {
        setLeaveTypes(response.data.leave_types || []);
      } else {
        setLeaveTypes([]);
      }
    } catch (error) {
      setLeaveTypes([]);
      console.error('Error fetching leave types:', error);
    }
  }, [API_BASE]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        await fetchUserPermissions();
        await Promise.all([
          fetchEmployees(),
          fetchLeaveMaster(),
          fetchLeaveTypes()
        ]);
      } catch (error) {
        console.error('Initialization error:', error);
        setError(`Initialization failed: ${error.message}`);
      }
    };

    initializeData();
  }, [fetchEmployees, fetchLeaveMaster, fetchLeaveTypes, fetchUserPermissions]);

  // Permission check functions
  const canViewLeaveMaster = () => userPermissions.can_view;
  const canAddLeaveMaster = () => userPermissions.can_add;
  const canEditLeaveMaster = () => userPermissions.can_edit;
  const canDeleteLeaveMaster = () => userPermissions.can_delete;

  const handleAdd = (employee) => {
    if (!canAddLeaveMaster()) {
      alert('You do not have permission to add leave allocations');
      return;
    }
    setSelectedEmployee(employee);
    setShowAddModal(true);
  };

  const handleEdit = (leaveMasterItem, employee) => {
    if (!canEditLeaveMaster()) {
      alert('You do not have permission to edit leave allocations');
      return;
    }
    setSelectedLeaveMaster(leaveMasterItem);
    setSelectedEmployee(employee);
    setShowEditModal(true);
  };

  const handleDelete = async (leaveId, empId) => {
    if (!canDeleteLeaveMaster()) {
      alert('You do not have permission to delete leave allocations');
      return;
    }

    if (window.confirm('Are you sure you want to delete this leave allocation?')) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await axios.post(`${API_BASE}leave/delete_leave_master.php`,
          { leave_id: leaveId, emp_id: empId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (response.data.success) {
          await fetchLeaveMaster();
          alert('Leave allocation deleted successfully');
        } else {
          setError(response.data.error || 'Failed to delete leave allocation');
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || 'Failed to delete leave allocation';
        setError(errorMsg);
        console.error('Error deleting leave allocation:', error);
      }
    }
  };

  const handleViewEmployee = (employee) => {
    navigate(`/employee-details/${employee.emp_id}`);
  };

  const getLeaveMasterForEmployee = (empId) => {
    return leaveMaster.filter(item => item.emp_id === empId);
  };

  const getLeaveTypeName = (leaveTypeId) => {
    const leaveType = leaveTypes.find(lt => lt.leave_type_id === leaveTypeId);
    return leaveType?.leave_name || `Leave Type ${leaveTypeId}`;
  };

  const getLeaveTypeCode = (leaveTypeId) => {
    const leaveType = leaveTypes.find(lt => lt.leave_type_id === leaveTypeId);
    return leaveType?.leave_code || '';
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesSearch =
      employee.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.emp_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      employee.email?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'All' || employee.status === filterStatus;

    return matchesSearch && matchesStatus;
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.first_name.localeCompare(b.first_name);
      case 'code':
        return a.emp_code.localeCompare(b.emp_code);
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return a.emp_id - b.emp_id;
    }
  });

  if ((loading && employees.length === 0) || !userPermissions.loaded) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">Loading leave master data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewLeaveMaster()) {
    return (
      <div className="container-fluid py-4">
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body text-center py-5">
                <i className="bi bi-shield-exclamation display-1 text-warning"></i>
                <h3 className="mt-3 text-warning">Access Denied</h3>
                <p className="text-muted">You do not have permission to view leave master data.</p>
                <button
                  className="btn btn-primary mt-3"
                  onClick={() => navigate('/dashboard')}
                >
                  <i className="bi bi-speedometer2 me-2"></i>
                  Back to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body py-4">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <div className="d-flex align-items-center">
                    <div className="btn-group me-3" role="group">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/dashboard')}
                        title="Go to Dashboard"
                      >
                        <i className="bi bi-speedometer2"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => navigate('/Menu')}
                        title="Go to Menu"
                      >
                        <i className="bi bi-menu-button"></i>
                      </button>
                    </div>
                    <div>
                      <h2 className="h4 mb-1 fw-bold text-dark">
                        <i className="bi bi-calendar-check me-2 text-success"></i>
                        Employee Leave Master
                        {!canAddLeaveMaster() && !canEditLeaveMaster() && !canDeleteLeaveMaster() && (
                          <span className="badge bg-warning ms-2">View Only</span>
                        )}
                      </h2>
                      <p className="text-muted mb-0">
                        Manage leave allocations and entitlements for employees
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 text-end">
                  <div className="d-flex justify-content-end align-items-center">
                    <button
                      className="btn btn-outline-secondary me-2"
                      onClick={() => navigate('/dashboard')}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Back to Dashboard
                    </button>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => navigate('/Menu')}
                    >
                      <i className="bi bi-file-earmark-text me-2"></i>
                      Menu
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-primary w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/admin-panel')}
                  >
                    <i className="bi bi-speedometer2 me-2"></i>
                    Admin Panel
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-success w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/employees')}
                  >
                    <i className="bi bi-people me-2"></i>
                    Employees
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-info w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/leave-type')}
                  >
                    <i className="bi bi-calendar2-week me-2"></i>
                    Manage Leave Types
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-warning w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/leave-request')}
                  >
                    <i className="bi bi-calendar-plus me-2"></i>
                    Applications
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-success w-100 d-flex align-items-center justify-content-center"
                    disabled
                  >
                    <i className="bi bi-calendar-check me-2"></i>
                    Leave Master
                  </button>
                </div>
                <div className="col-md-2 mb-2 mb-md-0">
                  <button
                    className="btn btn-outline-dark w-100 d-flex align-items-center justify-content-center"
                    onClick={() => navigate('/Menu')}
                  >
                    <i className="bi bi-menu-button me-2"></i>
                    Menu
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div>
                <strong>Error:</strong> {error}
                <div className="mt-2">
                  <button
                    className="btn btn-sm btn-outline-danger me-2"
                    onClick={fetchLeaveMaster}
                  >
                    <i className="bi bi-arrow-clockwise me-1"></i>
                    Retry
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setError('')}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      placeholder="Search employees..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <button
                        className="btn btn-outline-secondary border-start-0"
                        onClick={() => setSearchTerm('')}
                      >
                        <i className="bi bi-x"></i>
                      </button>
                    )}
                  </div>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                  >
                    <option value="All">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Resigned">Resigned</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <select
                    className="form-select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <option value="name">Sort by Name</option>
                    <option value="code">Sort by Employee Code</option>
                    <option value="status">Sort by Status</option>
                  </select>
                </div>
                <div className="col-md-2 text-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={fetchLeaveMaster}
                    title="Refresh"
                  >
                    <i className="bi bi-arrow-clockwise"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Employees List */}
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body p-0">
              {sortedEmployees.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-people display-1 text-muted"></i>
                  <h4 className="mt-3 text-muted">No employees found</h4>
                  <p className="text-muted">
                    {employees.length === 0
                      ? "No employees available. Please add employees first."
                      : "No employees match your search criteria."
                    }
                  </p>
                  {employees.length === 0 && (
                    <button
                      className="btn btn-primary mt-2"
                      onClick={() => navigate('/employees')}
                    >
                      <i className="bi bi-plus-circle me-2"></i>
                      Add Employees
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Employee</th>
                        <th>Contact</th>
                        <th>Status</th>
                        <th>Leave Summary</th>
                        <th className="text-end pe-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEmployees.map((employee) => {
                        const leaveMasterForEmp = getLeaveMasterForEmployee(employee.emp_id);
                        const totalLeaveTypes = leaveMasterForEmp.length;

                        return (
                          <React.Fragment key={employee.emp_id}>
                            {/* Employee Row */}
                            <tr className="employee-row">
                              <td className="ps-4">
                                <div className="d-flex align-items-center">
                                  <div className="employee-icon bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                    style={{ width: '40px', height: '40px' }}
                                  >
                                    <i className="bi bi-person"></i>
                                  </div>
                                  <div>
                                    <div className="fw-semibold">
                                      {employee.first_name} {employee.last_name}
                                    </div>
                                    <small className="text-muted">
                                      ID: {employee.emp_id} | Code: {employee.emp_code}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <div>
                                  <small className="text-muted d-block">{employee.email || 'No email'}</small>
                                  <small className="text-muted">{employee.phone || 'No phone'}</small>
                                </div>
                              </td>
                              <td>
                                <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                                  employee.status === 'Inactive' ? 'bg-secondary' :
                                    'bg-danger'
                                  }`}>
                                  {employee.status || 'Unknown'}
                                </span>
                              </td>
                              <td>
                                <div>
                                  <small className="text-muted d-block">
                                    Total Leave Types: {totalLeaveTypes}
                                  </small>
                                  <small className="text-muted d-block">
                                    Allocated Types: {leaveMasterForEmp.filter(l => l.status === 1).length}
                                  </small>
                                  {leaveMasterForEmp.length > 0 && (
                                    <small className="fw-bold text-success">
                                      Active Allocations: {leaveMasterForEmp.filter(l => l.status === 1).length}
                                    </small>
                                  )}
                                </div>
                              </td>
                              <td className="text-end pe-4">
                                <div className="btn-group" role="group">
                                  <button
                                    className="btn btn-outline-info btn-sm"
                                    onClick={() => handleViewEmployee(employee)}
                                    title="View Employee"
                                  >
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  {canAddLeaveMaster() && (
                                    <button
                                      className="btn btn-outline-success btn-sm"
                                      onClick={() => handleAdd(employee)}
                                      title="Add Leave Allocation"
                                    >
                                      <i className="bi bi-plus"></i>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>

                            {/* Leave Master Row - Expanded View */}
                            {leaveMasterForEmp.length > 0 && (
                              <tr className="bg-light">
                                <td colSpan="5" className="p-0">
                                  <div className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <h6 className="fw-bold text-success mb-0">
                                        <i className="bi bi-calendar-check me-2"></i>
                                        Leave Allocations ({leaveMasterForEmp.length})
                                      </h6>
                                      <small className="text-muted">
                                        Employee ID: {employee.emp_id}
                                      </small>
                                    </div>
                                    <div className="table-responsive">
                                      <table className="table table-sm">
                                        <thead>
                                          <tr>
                                            <th>Leave Type</th>
                                            <th>Leave Count</th>
                                            <th>Frequency</th>
                                            <th>Credited On</th>
                                            <th>Paid Leave</th>
                                            <th>Carry Forward</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {leaveMasterForEmp.map((item) => (
                                            <tr key={item.leave_id}>
                                              <td>
                                                <div className="d-flex align-items-center">
                                                  <div className="badge bg-info me-2">
                                                    {getLeaveTypeCode(item.leave_type_id)}
                                                  </div>
                                                  {getLeaveTypeName(item.leave_type_id)}
                                                </div>
                                              </td>
                                              <td>
                                                <span className="fw-semibold">{item.leave_count}</span> days
                                              </td>
                                              <td>
                                                <span className={`badge ${item.frequency === 'Monthly' ? 'bg-primary' : 'bg-warning'
                                                  }`}>
                                                  {item.frequency}
                                                </span>
                                              </td>
                                              <td>
                                                {item.credited_daymonth}
                                              </td>
                                              <td>
                                                <span className={`badge ${item.is_paid === 1 ? 'bg-success' : 'bg-secondary'
                                                  }`}>
                                                  {item.is_paid === 1 ? 'Yes' : 'No'}
                                                </span>
                                              </td>
                                              <td>
                                                <span className={`badge ${item.carry_forward === 1 ? 'bg-info' : 'bg-secondary'
                                                  }`}>
                                                  {item.carry_forward === 1 ? 'Allowed' : 'Not Allowed'}
                                                </span>
                                              </td>
                                              <td>
                                                <span className={`badge ${item.status === 1 ? 'bg-success' : 'bg-secondary'
                                                  }`}>
                                                  {item.status === 1 ? 'Active' : 'Inactive'}
                                                </span>
                                              </td>
                                              <td>
                                                <div className="btn-group btn-group-sm" role="group">
                                                  {canEditLeaveMaster() && (
                                                    <button
                                                      className="btn btn-outline-primary btn-sm"
                                                      onClick={() => handleEdit(item, employee)}
                                                      title="Edit Allocation"
                                                    >
                                                      <i className="bi bi-pencil"></i>
                                                    </button>
                                                  )}
                                                  {canDeleteLeaveMaster() && (
                                                    <button
                                                      className="btn btn-outline-danger btn-sm"
                                                      onClick={() => handleDelete(item.leave_id, employee.emp_id)}
                                                      title="Delete Allocation"
                                                    >
                                                      <i className="bi bi-trash"></i>
                                                    </button>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card bg-light border-0">
            <div className="card-body py-3">
              <div className="row text-center">
                <div className="col-md-3">
                  <div className="fw-bold text-primary fs-4">{employees.length}</div>
                  <small className="text-muted">Total Employees</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-success fs-4">
                    {employees.filter(e => e.status === 'Active').length}
                  </div>
                  <small className="text-muted">Active Employees</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-info fs-4">
                    {leaveMaster.length}
                  </div>
                  <small className="text-muted">Total Leave Allocations</small>
                </div>
                <div className="col-md-3">
                  <div className="fw-bold text-warning fs-4">
                    {employees.filter(e => getLeaveMasterForEmployee(e.emp_id).length > 0).length}
                  </div>
                  <small className="text-muted">Employees with Leave Allocations</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card border-0">
            <div className="card-body text-center py-3">
              <div className="btn-group" role="group">
                <button
                  className="btn btn-outline-primary"
                  onClick={fetchLeaveMaster}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  Refresh Data
                </button>
                <button
                  className="btn btn-outline-success"
                  onClick={() => navigate('/employees')}
                >
                  <i className="bi bi-people me-2"></i>
                  Manage Employees
                </button>
                <button
                  className="btn btn-outline-info"
                  onClick={() => navigate('/leave-types')}
                >
                  <i className="bi bi-calendar2-week me-2"></i>
                  Manage Leave Types
                </button>
                <button
                  className="btn btn-outline-warning"
                  onClick={fetchLeaveTypes}
                >
                  <i className="bi bi-calendar-check me-2"></i>
                  Refresh Leave Types
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showAddModal && selectedEmployee && canAddLeaveMaster() && (
        <AddLeaveMaster
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          employee={selectedEmployee}
          leaveTypes={leaveTypes}
          onLeaveMasterAdded={fetchLeaveMaster}
        />
      )}

      {showEditModal && selectedLeaveMaster && selectedEmployee && canEditLeaveMaster() && (
        <EditLeaveMaster
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          leaveMaster={selectedLeaveMaster}
          employee={selectedEmployee}
          leaveTypes={leaveTypes}
          onLeaveMasterUpdated={fetchLeaveMaster}
        />
      )}
    </div>
  );
};

export default LeaveMaster;